import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FeeCalendarCard } from "@/components/admin/FeeCalendarCard";
import { FeeRepairCard } from "@/components/admin/FeeRepairCard";
import { FeeCandidatesCard } from "@/components/admin/FeeCandidatesCard";

const STATUS = {
  possibly_not_offered: ["Needs your check", "bg-amber-100 text-amber-900"],
  confirmed_not_offered: ["Hidden: not offered", "bg-stone-800 text-white"],
  confirmed_offered: ["Kept: it exists", "bg-emerald-100 text-emerald-900"],
};

function ReviewRow({ r, busy, onDecide }) {
  const st = r.catalogue_review?.status;
  const [label, tone] = STATUS[st] || [st, "bg-stone-100"];
  const title = r.official_title || r.title || r.name;
  return (
    <li className="py-4 flex flex-col md:flex-row md:items-start gap-3" data-testid={`course-review-row-${r.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <Link to={`/degree/${r.id}`} className="font-medium text-stone-900 hover:underline" data-testid={`course-review-link-${r.id}`}>{title}</Link>
          <span className="text-xs text-stone-500">{r.university_name || r.university_id}</span>
          <span className={`text-[11px] px-2 py-0.5 rounded-full ${tone}`} data-testid={`course-review-status-${r.id}`}>{label}</span>
        </div>
        <p className="text-sm text-stone-600 mt-1 break-words">Evidence: “{r.catalogue_review?.evidence}”</p>
        {r.catalogue_review?.decided_by && <p className="text-xs text-stone-400 mt-1">Decided by {r.catalogue_review.decided_by}</p>}
      </div>
      <div className="flex flex-wrap gap-2 shrink-0">
        {st === "possibly_not_offered" ? (
          <>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => onDecide(r.id, "offered")} data-testid={`course-review-offered-${r.id}`}>It exists: keep</Button>
            <Button size="sm" disabled={busy} onClick={() => onDecide(r.id, "not_offered")} data-testid={`course-review-hide-${r.id}`}>Not offered: hide</Button>
          </>
        ) : (
          <Button size="sm" variant="outline" disabled={busy} onClick={() => onDecide(r.id, "undo")} data-testid={`course-review-undo-${r.id}`}>Undo</Button>
        )}
      </div>
    </li>
  );
}

const OUT_LABEL = { needs_review: "Needs your check", kept_after_review: "Kept", cleared_after_review: "Cleared" };
const fmtVal = (k, v) => (k.endsWith("employment_rate") && typeof v === "number" ? `${Math.round(v * (v <= 1 ? 100 : 1))}% employed`
  : k.endsWith("median_salary_usd") && typeof v === "number" ? `$${v.toLocaleString()} median salary` : null);

function OutcomeRow({ r, busy, onDecide }) {
  const rv = r.outcomes_review || {};
  const o = r.outcomes || {};
  const figs = (rv.fields || []).map((f) => fmtVal(f, o[f.split(".").pop()])).filter(Boolean);
  const quote = typeof o.quote === "string" ? o.quote : o.quote?.text;
  return (
    <li className="py-4 flex flex-col md:flex-row md:items-start gap-3" data-testid={`outcome-review-row-${r.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <Link to={`/degree/${r.id}`} className="font-medium text-stone-900 hover:underline">{r.official_title || r.name}</Link>
          <span className="text-xs text-stone-500">{r.university_id}</span>
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-900" data-testid={`outcome-review-status-${r.id}`}>{OUT_LABEL[rv.action] || rv.action}</span>
        </div>
        {figs.length > 0 && <p className="text-sm text-stone-800 mt-1">{figs.join(" · ")}</p>}
        {quote && <p className="text-sm text-stone-600 mt-1 break-words">Source text: “{String(quote).slice(0, 400)}”</p>}
        <p className="text-xs text-stone-400 mt-1">Why unsure: {rv.why}</p>
      </div>
      {rv.action === "needs_review" && (
        <div className="flex flex-wrap gap-2 shrink-0">
          <Button size="sm" variant="outline" disabled={busy} onClick={() => onDecide(r.id, "keep")} data-testid={`outcome-review-keep-${r.id}`}>Keep</Button>
          <Button size="sm" disabled={busy} onClick={() => onDecide(r.id, "clear")} data-testid={`outcome-review-clear-${r.id}`}>Clear</Button>
        </div>
      )}
    </li>
  );
}

function OutcomesQueue() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  useEffect(() => { api.get("/admin/course-review/outcomes").then(({ data }) => setRows(data.rows)).catch(() => setRows([])); }, []);
  const decide = async (id, decision) => {
    setBusy(id);
    try {
      const { data: row } = await api.post(`/admin/course-review/outcomes/${id}`, { decision });
      setRows((rs) => rs.map((r) => (r.id === id ? row : r)));
      toast.success(decision === "keep" ? "Kept" : "Cleared");
    } catch { toast.error("Could not save"); }
    finally { setBusy(null); }
  };
  if (!rows) return null;
  const waiting = rows.filter((r) => r.outcomes_review?.action === "needs_review").length;
  return (
    <section className="mt-12" data-testid="outcome-review-section">
      <h2 className="text-lg font-semibold text-stone-900">Unsure graduate-outcome figures</h2>
      <p className="text-sm text-stone-600 mt-1">Figures the scraper saved where it couldn't tell if they belong to this course. {waiting} waiting.</p>
      <ul className="divide-y divide-stone-200 mt-2">
        {rows.map((r) => <OutcomeRow key={r.id} r={r} busy={busy === r.id} onDecide={decide} />)}
      </ul>
    </section>
  );
}

export default function AdminCourseReview() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    setError(null);
    try { setData((await api.get("/admin/course-review")).data); }
    catch (e) { setError(e?.response?.status === 403 ? "Admins only." : "Could not load the queue."); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const decide = async (id, decision) => {
    setBusy(id);
    try {
      const { data: row } = await api.post(`/admin/course-review/${id}`, { decision });
      setData((d) => ({ ...d, rows: d.rows.map((r) => (r.id === id ? row : r)) }));
      toast.success(decision === "not_offered" ? "Hidden from searches" : decision === "offered" ? "Kept" : "Undone");
    } catch { toast.error("Could not save"); }
    finally { setBusy(null); }
  };
  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="course-review-page">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-semibold text-stone-900">Courses that may not exist</h1>
          <p className="text-sm text-stone-600 mt-2 max-w-2xl">
            Flagged from the scraper's own notes; one page isn't proof. "Not offered: hide" removes the course from every search and list.
            Its page stays reachable with a notice, so saved shortlists keep working. Undo restores it.
          </p>
        </div>
        <button onClick={load} className="text-sm text-stone-500 hover:text-stone-900 flex items-center gap-1" data-testid="course-review-refresh">
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>
      {error && <p className="text-sm text-red-700" data-testid="course-review-error">{error}</p>}
      {!data && !error && <Loader2 className="h-5 w-5 animate-spin text-stone-400" data-testid="course-review-loading" />}
      {data && (
        <>
          <p className="text-xs text-stone-500" data-testid="course-review-counts">
            {Object.entries(data.counts).map(([k, n]) => `${(STATUS[k] || [k])[0]}: ${n}`).join(" · ")}
          </p>
          <ul className="divide-y divide-stone-200 mt-2">
            {data.rows.map((r) => <ReviewRow key={r.id} r={r} busy={busy === r.id} onDecide={decide} />)}
          </ul>
        </>
      )}
      <OutcomesQueue />
      <FeeCalendarCard />
      <FeeRepairCard />
      <FeeCandidatesCard />
    </div>
  );
}
